import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Cloud, Server, ShieldCheck, Database, Globe, Lock } from "lucide-react";

// --- Helper Components ---

const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${i <= rating ? "text-yellow-500" : "text-gray-300"} inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2 border border-gray-300">
    <ShieldCheck className="w-3 h-3 mr-1 text-orange-500" />
    Verified
  </span>
);

const ToolsYouWillMaster = () => {
  const tools = [
    { name: 'EC2', logo: 'https://static-00.iconduck.com/assets.00/aws-ec2-icon-1662x2048-b981k680.png' },
    { name: 'S3 Bucket', logo: 'https://upload.wikimedia.org/wikipedia/commons/b/bc/Amazon-S3-Logo.svg' },
    { name: 'Lambda', logo: 'https://upload.wikimedia.org/wikipedia/commons/8/8f/AWS-Lambda-Logo.svg' },
    { name: 'IAM', logo: 'https://static-00.iconduck.com/assets.00/aws-iam-icon-1582x2048-2er2622f.png' },
    { name: 'VPC', logo: 'https://static-00.iconduck.com/assets.00/aws-vpc-icon-2048x2048-q9e2363a.png' },
    { name: 'RDS', logo: 'https://static-00.iconduck.com/assets.00/aws-rds-icon-1748x2048-15573752.png' },
    { name: 'CloudWatch', logo: 'https://static-00.iconduck.com/assets.00/aws-cloudwatch-icon-1667x2048-26217915.png' },
    { name: 'Route 53', logo: 'https://static-00.iconduck.com/assets.00/aws-route53-icon-1634x2048-27218321.png' },
  ];

  return (
    <section className="py-20 bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-black font-aligran">
          AWS Services You Will <span className="text-orange-500">Master</span>
        </h2>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-8 mb-8">
          {tools.map((tool, i) => (
            <motion.div
              key={i}
              className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <img src={tool.logo} alt={tool.name} className="w-16 h-16 mb-2 object-contain" />
              <p className="text-sm font-semibold text-black mt-2">{tool.name}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AWSCourse = () => {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [formData, setFormData] = useState({ firstName: "", businessEmail: "", phone: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Inquiry Sent!");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % 5);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    { id: 1, name: "Rohit Malhotra", review: "I cleared the AWS Solutions Architect Associate exam in the first attempt. The mock tests were very accurate.", rating: 5, profileImg: "https://randomuser.me/api/portraits/men/32.jpg", source: "Google Reviews" },
    { id: 2, name: "Sneha Kapoor", review: "VPC and Subnetting were confusing for me, but the hands-on labs made it crystal clear. Great training!", rating: 5, profileImg: "https://randomuser.me/api/portraits/women/44.jpg", source: "LinkedIn" },
    { id: 3, name: "Amit Singh", review: "EC2 and S3 modules are very detailed. I learned how to host a static website on S3 in just 10 mins.", rating: 4, profileImg: "https://randomuser.me/api/portraits/men/50.jpg", source: "Website Feedback" },
    { id: 4, name: "Priya Das", review: "The instructor explained IAM roles and policies very well. Security is a big part of this course.", rating: 5, profileImg: "https://randomuser.me/api/portraits/women/60.jpg", source: "Google Reviews" },
    { id: 5, name: "Vikram Singh", review: "Best AWS training in Noida. They cover everything from basic EC2 to advanced Lambda functions.", rating: 4, profileImg: "https://randomuser.me/api/portraits/men/12.jpg", source: "LinkedIn" },
  ];

  const currentVisibleTestimonials = [
    testimonials[currentTestimonialIndex],
    testimonials[(currentTestimonialIndex + 1) % 5],
    testimonials[(currentTestimonialIndex + 2) % 5],
  ];

  const curriculum = [
    { title: "Module 1: Cloud Computing Basics", description: "What is Cloud? IaaS, PaaS, SaaS models. AWS Global Infrastructure (Regions, AZs)." },
    { title: "Module 2: Identity & Access Mgmt (IAM)", description: "Creating Users, Groups, Roles, Policies, and MFA for secure access." },
    { title: "Module 3: Compute Services (EC2)", description: "Launching Instances, Security Groups, Key Pairs, EBS Volumes, and Load Balancers (ELB)." },
    { title: "Module 4: Storage Services (S3)", description: "Buckets, Objects, Versioning, S3 Glacier for archives, and Hosting Static Websites." },
    { title: "Module 5: Networking (VPC)", description: "Creating custom VPC, Subnets, Internet Gateways, Route Tables, and NAT Gateways." },
    { title: "Module 6: Database Services (RDS)", description: "Setting up MySQL/Postgres on Cloud, Multi-AZ deployments, and Read Replicas." },
    { title: "Module 7: Serverless (Lambda)", description: "Introduction to Serverless computing, writing Lambda functions, and triggers." },
    { title: "Module 8: Monitoring (CloudWatch)", description: "Setting up Alarms, Logs, and Events to monitor infrastructure health." },
    { title: "Module 9: Auto Scaling & Route 53", description: "Dynamic scaling of EC2 instances and DNS management with Route 53." },
  ];

  const highlights = [
    { title: 'Global Leader', desc: 'AWS owns 33% of Cloud market.', img: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072' },
    { title: 'Certification', desc: 'SAA-C03 Exam Prep.', img: 'https://images.unsplash.com/photo-1589330273594-fade1ee91647?q=80&w=2070' },
    { title: 'Live Servers', desc: 'Launch real EC2 instances.', img: 'https://images.unsplash.com/photo-1558494949-ef526b0042a0?q=80&w=2070' },
    { title: 'Networking', desc: 'Design your own VPC.', img: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?q=80&w=2070' },
    { title: 'Storage', desc: 'S3 & Glacier mastery.', img: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070' },
    { title: 'Serverless', desc: 'Code without servers (Lambda).', img: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=2070' },
    { title: 'Expert Mentors', desc: 'AWS Certified Architects.', img: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070' },
    { title: 'Job Ready', desc: 'High demand for Cloud Architects.', img: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=2069' },
    { title: 'Security', desc: 'IAM & Encryption standards.', img: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2070' },
  ];

  return (
    <div className="bg-white text-gray-900 font-sans">
      <Helmet>
        <title>AWS Cloud Course | Solutions Architect Certification</title>
        <meta name="description" content="Master Amazon Web Services (AWS). Learn EC2, S3, VPC, and Lambda for Certification." />
      </Helmet>

      {/* Hero */}
      <section className="relative h-[550px] flex items-center bg-black">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1523961131990-5ea7c61b2107?q=80&w=2074"
            alt="AWS Cloud"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-orange-900/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center w-full pt-20">
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }}>
            <span className="inline-block px-3 py-1 mb-4 border border-orange-500 rounded-full bg-orange-900/30 text-orange-400 text-sm font-semibold">
              CLOUD LEADER
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white font-aligran">
              Master <span className="text-orange-500 block">AWS Cloud</span>
            </h1>
            <p className="text-xl text-gray-300 mt-6 mb-8">
              Learn the world's #1 Cloud Platform. Design scalable, secure, and reliable infrastructure on Amazon Web Services.
            </p>
            <button
              onClick={() => document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-4 px-10 rounded-lg shadow-lg hover:scale-105 transition"
            >
              Start Cloud Journey
            </button>
          </motion.div>
          <motion.div className="flex justify-end">
            <Cloud size={220} className="text-orange-500 opacity-80 drop-shadow-lg" />
          </motion.div>
        </div>
      </section>

      {/* Why Learn */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50 text-center">
        <h2 className="text-4xl font-extrabold mb-14 font-aligran">
          Why Learn <span className="text-orange-500">AWS?</span>
        </h2>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          <div className="p-8 border border-gray-100 rounded-2xl bg-white shadow-md hover:-translate-y-2 transition duration-300">
            <Globe className="mx-auto mb-4 text-orange-600" size={40} />
            <h3 className="font-bold text-xl mb-3">Market Leader</h3>
            <p className="text-gray-600">AWS holds the largest market share in the cloud industry, meaning more job opportunities.</p>
          </div>
          <div className="p-8 border border-gray-100 rounded-2xl bg-white shadow-md hover:-translate-y-2 transition duration-300">
            <Server className="mx-auto mb-4 text-blue-600" size={40} />
            <h3 className="font-bold text-xl mb-3">Scalability</h3>
            <p className="text-gray-600">Companies like Netflix, Facebook, and Twitch run on AWS infrastructure.</p>
          </div>
          <div className="p-8 border border-gray-100 rounded-2xl bg-white shadow-md hover:-translate-y-2 transition duration-300">
            <Lock className="mx-auto mb-4 text-green-600" size={40} />
            <h3 className="font-bold text-xl mb-3">Security</h3>
            <p className="text-gray-600">AWS provides enterprise-grade security, making it the top choice for banking and finance.</p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
            Course <span className="text-orange-500">Highlights</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {highlights.map((h, i) => (
              <div
                key={i}
                className="bg-white rounded-xl shadow-md overflow-hidden flex h-40 hover:shadow-xl transition hover:-translate-y-1"
              >
                <div className="w-1/2 p-4 flex flex-col justify-center">
                  <h3 className="font-bold text-lg">{h.title}</h3>
                  <p className="text-sm text-gray-600">{h.desc}</p>
                </div>
                <div className="w-1/2 relative">
                  <img src={h.img} alt={h.title} className="w-full h-full object-cover" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 font-aligran">
            Detailed <span className="text-orange-500">Curriculum</span>
          </h2>
          <div className="space-y-4">
            {curriculum.map((m, i) => (
              <details key={i} className="group border rounded-lg bg-gray-50">
                <summary className="cursor-pointer px-6 py-4 font-semibold flex justify-between">
                  {m.title}
                  <span className="group-open:rotate-180 transition">▼</span>
                </summary>
                <div className="px-6 py-4 border-t text-gray-700">{m.description}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Tools */}
      <ToolsYouWillMaster />

      {/* FAQ & Form */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl font-bold mb-8 font-aligran">FAQ</h2>
          <div className="space-y-4">
            <details className="bg-gray-50 border p-4 rounded-lg">
              <summary className="font-bold">Is coding required?</summary>
              <p className="mt-2 text-sm">Minimal. AWS is mostly about configuration and architecture, though some JSON/Python helps.</p>
            </details>
            <details className="bg-gray-50 border p-4 rounded-lg">
              <summary className="font-bold">Does this cover Certification?</summary>
              <p className="mt-2 text-sm">Yes, this course strictly follows the AWS Certified Solutions Architect - Associate syllabus.</p>
            </details>
          </div>
        </div>
        <div className="md:col-span-1" id="enquiry-form">
          <div className="bg-gray-100 border border-orange-300 rounded-xl p-6 shadow-xl sticky top-10">
            <h3 className="text-2xl font-bold mb-2 text-center text-orange-600">Book Seat</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                className="w-full border p-3 rounded"
                placeholder="Name"
                name="firstName"
                onChange={handleInputChange}
              />
              <input
                className="w-full border p-3 rounded"
                placeholder="Email"
                name="businessEmail"
                onChange={handleInputChange}
              />
              <input
                className="w-full border p-3 rounded"
                placeholder="Phone"
                name="phone"
                onChange={handleInputChange}
              />
              <button className="w-full bg-orange-600 text-white font-bold py-3 rounded hover:bg-orange-700 transition">
                ENQUIRE
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-gray-50 py-20 border-t">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 font-aligran">
            Success <span className="text-orange-500">Stories</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {currentVisibleTestimonials.map((t) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-white p-6 rounded-xl border shadow-sm"
                >
                  <div className="flex items-center mb-4">
                    <img
                      src={t.profileImg}
                      className="w-12 h-12 rounded-full border-2 border-orange-500 mr-3"
                      alt={t.name}
                    />
                    <div>
                      <h4 className="font-bold">{t.name}</h4>
                      <span className="text-xs text-gray-500">{t.source}</span>
                    </div>
                  </div>
                  <StarRating rating={t.rating} />
                  <p className="text-gray-700 text-sm mt-2">"{t.review}"</p>
                  <VerifiedBadge />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AWSCourse;